import { Box, Card, CardContent, Grid, Typography } from "@mui/material"
import React, { ChangeEvent, useCallback, useContext } from "react"
import { customPalette } from "../../../../../../../../../../../../theme"
import { FormNumberInput } from "../../../../../../../../../../../components/inputs/number-input/FormNumberInput"
import { BooleanRadioButtonInput } from "../../../../../../../../../../../components/inputs/radio-button-input/BooleanRadioButtonInput"
import { FormSelectInput } from "../../../../../../../../../../../components/inputs/select-input/FormSelectInput"
import { FlatTypeEnum, flatTypeEnumLabels } from "../../../../../../../../../../../core/enum/beem-shot/FlatTypeEnum"
import { OfficeTypeEnum, officeTypeEnumLabels } from "../../../../../../../../../../../core/enum/beem-shot/OfficeTypeEnum"
import { SchoolTypeEnum, schoolTypeEnumLabels } from "../../../../../../../../../../../core/enum/beem-shot/SchoolTypeEnum"
import { UsageEnum, usageEnumLabels } from "../../../../../../../../../../../core/enum/usageEnum"
import { enumToSelectOptions } from "../../../../../../../../../../../core/services/helper-service"
import { BSBuildingContext } from "../../../../../components/context/BSBuildingContext"
import { BSUsageContext, IBSUsageForm } from "../../../context/BSUsageContext"

export function BSBuildingPrincipalUsageForm(): React.JSX.Element {
  const { selectedBSBuilding } = useContext(BSBuildingContext)
  const { form, handleChange, errors } = useContext(BSUsageContext)

  const handlePourcentageFieldChange: (event: ChangeEvent<HTMLInputElement>, linkedField: keyof IBSUsageForm) => void =
    useCallback(
      (event, linkedField) => {
        const target = event.target
        const fieldName = target.id
        const value = Math.max(0, Math.min(100, Number(event.target.value)))

        const linkedFieldValue = 100 - value

        const syntheticEvent1 = {
          target: {
            id: fieldName,
            value: value.toString(),
            type: "number",
          },
        } as React.ChangeEvent<HTMLInputElement>

        const syntheticEvent2 = {
          target: {
            id: linkedField,
            value: linkedFieldValue.toString(),
            type: "number",
          },
        } as React.ChangeEvent<HTMLInputElement>

        handleChange(syntheticEvent1)
        handleChange(syntheticEvent2)
      },
      [handleChange]
    )

  return (
    <Card
      sx={{
        backgroundColor: customPalette.white,
        borderRadius: 3,
        mb: 3,
        width: "100%",
        boxShadow: "2px 4px 20px 0px rgba(0, 0, 0, 0.05)",
      }}>
      <CardContent>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            p: 1,
            mb: 1,
          }}>
          <Typography variant="body2" fontWeight={600} sx={{ color: customPalette.darkBlue2 }}>
            Usage principal
          </Typography>
          <Box
            sx={{
              flexGrow: 1,
              height: "2px",
              backgroundColor: customPalette.lightBlue1,
              marginLeft: "8px",
            }}
          />
        </Box>

        <Grid container columnSpacing={1}>
          <Grid item xs={4}>
            <FormSelectInput
              id="usage"
              label="Typologie d'usage"
              mode="event"
              selectedOption={form.usage}
              options={enumToSelectOptions(UsageEnum, usageEnumLabels).filter((option) => {
                if (selectedBSBuilding?.bim) {
                  return option.value !== UsageEnum.MAISONS_INDIVIDUELLES_OU_ACCOLEES
                } else {
                  return (
                    option.value !== UsageEnum.MAISONS_INDIVIDUELLES_OU_ACCOLEES &&
                    option.value !== UsageEnum.ENSEIGNEMENT_PRIMAIRE_OU_SECONDAIRE
                  )
                }
              })}
              handleEventChange={handleChange}
              errors={errors}
            />
          </Grid>
          {form.usage === UsageEnum.LOGEMENTS_COLLECTIFS && !form.nombreLogement && (
            <Grid item xs={4}>
              <FormSelectInput
                id="typeLogement"
                label="Type de logements"
                selectedOption={form.typeLogement}
                options={enumToSelectOptions(FlatTypeEnum, flatTypeEnumLabels)}
                mode="event"
                handleEventChange={handleChange}
                errors={errors}
              />
            </Grid>
          )}
          {form.usage === UsageEnum.BUREAUX && (
            <>
              <Grid item xs={4}>
                <FormSelectInput
                  id="typeBureau"
                  label="Type de bureaux"
                  mode="event"
                  selectedOption={form.typeBureau}
                  options={enumToSelectOptions(OfficeTypeEnum, officeTypeEnumLabels)}
                  handleEventChange={handleChange}
                  isBeemShot
                  errors={errors}
                />
              </Grid>

              <Grid item xs={4} />

              {(form.typeBureau === OfficeTypeEnum.MIXTE || form.typeBureau === OfficeTypeEnum.CLOISONNE) && (
                <Grid item xs={4}>
                  <FormNumberInput
                    fieldName="surfaceUnitaireBureaux"
                    label="Surface unitaire de bureaux (en m²)"
                    handleEventChange={handleChange}
                    form={form}
                    errors={errors}
                  />
                </Grid>
              )}

              {form.typeBureau === OfficeTypeEnum.MIXTE && (
                <>
                  <Grid item xs={4}>
                    <FormNumberInput
                      fieldName="pourcentageOpenSpace"
                      label="Part d'open space (en %)"
                      handleEventChange={(e) => handlePourcentageFieldChange(e, "pourcentageCloisonne")}
                      form={form}
                      errors={errors}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <FormNumberInput
                      fieldName="pourcentageCloisonne"
                      label="Part de cloison (en %)"
                      handleEventChange={(e) => handlePourcentageFieldChange(e, "pourcentageOpenSpace")}
                      form={form}
                      errors={errors}
                    />
                  </Grid>
                </>
              )}
            </>
          )}

          {form.usage === UsageEnum.ENSEIGNEMENT_PRIMAIRE_OU_SECONDAIRE && (
            <Grid item xs={4}>
              <FormSelectInput
                id="typeEnseignement"
                label="Type d'enseignement"
                mode="event"
                selectedOption={form.typeEnseignement}
                options={enumToSelectOptions(SchoolTypeEnum, schoolTypeEnumLabels)}
                handleEventChange={handleChange}
                isBeemShot
                errors={errors}
              />
            </Grid>
          )}
        </Grid>

        {form.usage === UsageEnum.LOGEMENTS_COLLECTIFS && (
          <>
            <Grid item xs={12}>
              <BooleanRadioButtonInput
                filedName="nombreLogement"
                label="Connaissez-vous le nombre de logements exact par typologie ?"
                value={form.nombreLogement}
                handleChange={handleChange}
              />
            </Grid>
            {form.nombreLogement && (
              <Grid container columnSpacing={1}>
                <Grid item xs={4}>
                  <FormNumberInput
                    fieldName="numberT1"
                    label="Nombre de T1"
                    handleEventChange={handleChange}
                    form={form}
                    errors={errors}
                  />
                </Grid>
                <Grid item xs={4}>
                  <FormNumberInput
                    fieldName="numberT2"
                    label="Nombre de T2"
                    handleEventChange={handleChange}
                    form={form}
                    errors={errors}
                  />
                </Grid>
                <Grid item xs={4}>
                  <FormNumberInput
                    fieldName="numberT3"
                    label="Nombre de T3"
                    handleEventChange={handleChange}
                    form={form}
                    errors={errors}
                  />
                </Grid>
                <Grid item xs={4}>
                  <FormNumberInput
                    fieldName="numberT4"
                    label="Nombre de T4"
                    handleEventChange={handleChange}
                    form={form}
                    errors={errors}
                  />
                </Grid>
                <Grid item xs={4}>
                  <FormNumberInput
                    fieldName="numberT5"
                    label="Nombre de T5"
                    handleEventChange={handleChange}
                    form={form}
                    errors={errors}
                  />
                </Grid>
              </Grid>
            )}
          </>
        )}
        {selectedBSBuilding?.bim && (
          <Grid item xs={6}>
            <FormNumberInput
              fieldName="nombreEtage"
              label="Nombre de niveaux en superstructure"
              handleEventChange={handleChange}
              form={form}
              errors={errors}
            />
          </Grid>
        )}

        {selectedBSBuilding?.bim && (
          <Grid item xs={6}>
            <FormNumberInput
              fieldName="hauteurMoyenneEtage"
              label="Hauteur moyenne d'un étage"
              handleEventChange={handleChange}
              form={form}
              errors={errors}
            />
          </Grid>
        )}
      </CardContent>
    </Card>
  )
}
